import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type { RosterResult, RosterEntry, MarkAttendancePayload, AttendanceSummaryRow, AttendancePolicy, CalendarDay, CalendarDayType, RequiredDays, ClassEligibility, StudentLog, GraceEntry, GraceStatus, GraceType, ExamEligibility, SubjectOption, SubjectRule, AttendanceOverview, IntegrityReport, ClassLogDay } from "../types";

const ATTENDANCE = INSTITUTION_ADMIN_ENDPOINTS.ATTENDANCE;

export async function fetchRoster(
  institutionId: number,
  params: { academicGroupId: number; academicYearId: number; date: string }
): Promise<RosterResult> {
  const { data } = await privateClient.get(ATTENDANCE.ROSTER(institutionId), { params });
  return { roster: data.roster ?? [], marked: !!data.marked, dateIssue: data.dateIssue ?? null };
}

export async function markAttendance(institutionId: number, payload: MarkAttendancePayload): Promise<RosterResult> {
  const { data } = await privateClient.post(ATTENDANCE.MARK(institutionId), payload);
  return { roster: data.roster ?? [], marked: !!data.marked, dateIssue: data.dateIssue ?? null };
}

export async function fetchSummary(
  institutionId: number,
  params: { academicGroupId?: number; from?: string; to?: string }
): Promise<AttendanceSummaryRow[]> {
  const { data } = await privateClient.get(ATTENDANCE.SUMMARY(institutionId), { params });
  return data.summary ?? [];
}

export async function fetchAttendancePolicy(institutionId: number): Promise<AttendancePolicy> {
  const { data } = await privateClient.get(ATTENDANCE.POLICY(institutionId));
  return data.policy;
}

export async function saveAttendancePolicy(institutionId: number, patch: Partial<Omit<AttendancePolicy, "grace">> & { grace?: Partial<AttendancePolicy["grace"]> }): Promise<AttendancePolicy> {
  const { data } = await privateClient.put(ATTENDANCE.POLICY(institutionId), patch);
  return data.policy;
}

export async function fetchCalendar(institutionId: number, from: string, to: string): Promise<CalendarDay[]> {
  const { data } = await privateClient.get(ATTENDANCE.CALENDAR(institutionId), { params: { from, to } });
  return data.days ?? [];
}

export async function saveCalendarDay(institutionId: number, day: { date: string; dayType: CalendarDayType; label?: string }): Promise<void> {
  await privateClient.put(ATTENDANCE.CALENDAR(institutionId), day);
}

export async function removeCalendarDay(institutionId: number, date: string): Promise<void> {
  await privateClient.delete(ATTENDANCE.CALENDAR_DAY(institutionId, date));
}

export async function fetchRequiredDays(institutionId: number, academicYearId: number, upTo?: string): Promise<RequiredDays> {
  const { data } = await privateClient.get(ATTENDANCE.REQUIRED_DAYS(institutionId), { params: { academicYearId, upTo } });
  return data;
}

export async function fetchClassEligibility(institutionId: number, academicGroupId: number, academicYearId: number): Promise<ClassEligibility> {
  const { data } = await privateClient.get(ATTENDANCE.ELIGIBILITY(institutionId), { params: { academicGroupId, academicYearId } });
  return data;
}

export async function fetchStudentLog(
  institutionId: number,
  studentId: number,
  params: { academicGroupId: number; academicYearId: number; from: string; to: string }
): Promise<StudentLog> {
  const { data } = await privateClient.get(ATTENDANCE.STUDENT_LOG(institutionId, studentId), { params });
  return data;
}

export async function fetchGrace(institutionId: number, params: { academicYearId: number; academicGroupId?: number; status?: GraceStatus }): Promise<GraceEntry[]> {
  const { data } = await privateClient.get(ATTENDANCE.GRACE(institutionId), { params });
  return data.grace ?? [];
}

export async function addGrace(
  institutionId: number,
  body: { academicGroupId: number; academicYearId: number; studentId: number; subjectId?: number; graceType: GraceType; creditDays: number; reason: string }
): Promise<{ id: number; status: GraceStatus }> {
  const { data } = await privateClient.post(ATTENDANCE.GRACE(institutionId), body);
  return data;
}

export async function reviewGrace(institutionId: number, graceId: number, decision: "APPROVED" | "REJECTED", note?: string): Promise<void> {
  await privateClient.post(ATTENDANCE.GRACE_REVIEW(institutionId, graceId), { decision, note });
}

export async function fetchExamEligibility(institutionId: number, examId: number, academicGroupId: number): Promise<ExamEligibility> {
  const { data } = await privateClient.get(ATTENDANCE.EXAM_ELIGIBILITY(institutionId, examId), { params: { academicGroupId } });
  return data;
}

export async function setExamOverride(institutionId: number, examId: number, studentId: number, body: { academicGroupId: number; allowed: boolean; reason: string }): Promise<void> {
  await privateClient.put(ATTENDANCE.EXAM_OVERRIDE(institutionId, examId, studentId), body);
}

export async function clearExamOverride(institutionId: number, examId: number, studentId: number, academicGroupId: number): Promise<void> {
  await privateClient.delete(ATTENDANCE.EXAM_OVERRIDE(institutionId, examId, studentId), { params: { academicGroupId } });
}

export async function freezeExamEligibility(institutionId: number, examId: number, academicGroupId: number): Promise<ExamEligibility> {
  const { data } = await privateClient.post(ATTENDANCE.EXAM_FREEZE(institutionId, examId), { academicGroupId });
  return data;
}

export async function unfreezeExamEligibility(institutionId: number, examId: number, academicGroupId: number): Promise<ExamEligibility> {
  const { data } = await privateClient.delete(ATTENDANCE.EXAM_FREEZE(institutionId, examId), { params: { academicGroupId } });
  return data;
}

export async function fetchSubjects(institutionId: number, academicGroupId: number, academicYearId: number): Promise<SubjectOption[]> {
  const { data } = await privateClient.get(ATTENDANCE.SUBJECTS(institutionId), { params: { academicGroupId, academicYearId } });
  return data.subjects ?? [];
}

export async function fetchSubjectRoster(
  institutionId: number,
  params: { academicGroupId: number; academicYearId: number; subjectId: number; date: string }
): Promise<{ marked: boolean; roster: RosterEntry[]; dateIssue?: string | null }> {
  const { data } = await privateClient.get(ATTENDANCE.SUBJECT_ROSTER(institutionId), { params });
  return data;
}

export async function markSubjectAttendance(
  institutionId: number,
  body: { academicGroupId: number; academicYearId: number; subjectId: number; date: string; records: { studentId: number; status: string }[] }
): Promise<{ marked: boolean; roster: RosterEntry[]; dateIssue?: string | null }> {
  const { data } = await privateClient.post(ATTENDANCE.SUBJECT_MARK(institutionId), body);
  return data;
}

export async function fetchSubjectRules(institutionId: number, academicYearId: number): Promise<SubjectRule[]> {
  const { data } = await privateClient.get(ATTENDANCE.SUBJECT_RULES(institutionId), { params: { academicYearId } });
  return data.rules ?? [];
}

export async function saveSubjectRule(institutionId: number, body: { academicYearId: number; subjectId: number; minPercent: number | null }): Promise<void> {
  await privateClient.put(ATTENDANCE.SUBJECT_RULES(institutionId), body);
}

/** Admin: undo a roll call taken by mistake. Pass subjectId for a subject-wise class; omit it for a daily class-day. */
export async function cancelAttendanceSession(institutionId: number, params: { academicGroupId: number; academicYearId: number; date: string; subjectId?: number }): Promise<void> {
  await privateClient.delete(ATTENDANCE.SESSION(institutionId), { params });
}

export async function fetchOverview(institutionId: number, academicYearId: number, date?: string): Promise<AttendanceOverview> {
  const { data } = await privateClient.get(ATTENDANCE.OVERVIEW(institutionId), { params: { academicYearId, date } });
  return data;
}

export async function verifyAttendanceCounters(institutionId: number, academicYearId: number): Promise<IntegrityReport> {
  const { data } = await privateClient.get(ATTENDANCE.INTEGRITY(institutionId), { params: { academicYearId } });
  return data;
}

export async function rebuildAttendanceCounters(institutionId: number, academicYearId: number): Promise<{ fixed: number }> {
  const { data } = await privateClient.post(ATTENDANCE.INTEGRITY_REBUILD(institutionId), { academicYearId });
  return data;
}

/** A class's marked days in a date range, with every student's status — the raw material for a printed/exported register. */
export async function fetchClassLog(institutionId: number, params: { academicGroupId: number; academicYearId: number; from: string; to: string }): Promise<ClassLogDay[]> {
  const { data } = await privateClient.get(ATTENDANCE.CLASS_LOG(institutionId), { params });
  return data.days ?? [];
}
